import { useCallback, useEffect, useState } from 'react';
import { api, useSocket, useSocketEvent } from '@mobi/web';
import type { LatLng } from '@mobi/shared';

export interface RideParty {
  id: number;
  name: string;
  phone: string | null;
  rating: number | null;
  ratingCount: number;
  /** o servidor só marca true enquanto a foto pode ser vista */
  hasPhoto: boolean;
  vehicle: { plate: string; model: string; color: string; type: string } | null;
}

export interface Ride {
  id: number;
  status: string;
  vehicleType: 'car' | 'moto';
  origin: LatLng & { address: string };
  destination: LatLng & { address: string };
  estimate: { distanceMeters: number; durationSeconds: number; fareCents: number };
  finalFareCents: number | null;
  walletAppliedCents: number;
  cashDueCents: number | null;
  routePolyline: string | null;
  shareToken: string | null;
  cancelledBy: string | null;
  /** só vem na listagem: se QUEM pediu a lista já avaliou esta corrida */
  rated?: boolean;
  driver: RideParty | null;
  passenger: RideParty | null;
  timestamps: Record<string, string | null>;
}

/**
 * Estado da corrida ativa.
 *
 * O WebSocket avisa que algo mudou; o estado vem sempre do REST. Um evento
 * perdido no elevador não pode deixar a tela mentindo — e um evento
 * duplicado não pode adiantar a viagem.
 */
export function useRide() {
  const [ride, setRide] = useState<Ride | null>(null);
  const [loading, setLoading] = useState(true);
  // O heading vem junto e é o que gira o ícone do veículo no mapa.
  const [driverPosition, setDriverPosition] = useState<(LatLng & { heading?: number }) | null>(
    null,
  );
  const { socket, connected } = useSocket(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api<{ ride: Ride | null }>('/api/rides/active');
      setRide(res.ride);
      if (!res.ride) setDriverPosition(null);
    } catch {
      // Sem rede: mantém a última tela conhecida em vez de piscar vazio.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Reconectou depois de perder sinal: reconcilia com o servidor.
  useEffect(() => {
    if (connected) void refresh();
  }, [connected, refresh]);

  useSocketEvent(socket, 'ride:status', () => void refresh());
  useSocketEvent(socket, 'ride:accepted', () => void refresh());
  useSocketEvent(socket, 'ride:driver_arrived', () => void refresh());
  useSocketEvent(socket, 'ride:started', () => void refresh());
  useSocketEvent(socket, 'ride:finished', () => void refresh());
  useSocketEvent(socket, 'ride:rejoined', () => void refresh());

  useSocketEvent<{ lat: number; lng: number; heading?: number }>(
    socket,
    'ride:driver_position',
    (p) => setDriverPosition({ lat: p.lat, lng: p.lng, heading: p.heading }),
  );

  return { ride, loading, driverPosition, refresh, socket, connected };
}
